import { useEffect } from "react";
import useSeo from "@/lib/useSeo";
import { track } from "@/lib/api";

const Section = ({ id, title, children }) => (
  <section id={id} data-testid={`privacy-section-${id}`} className="scroll-mt-28">
    <h2 className="text-xl md:text-2xl font-serif font-semibold text-emerald-950">{title}</h2>
    <div className="mt-3 space-y-3 text-slate-600 leading-relaxed">{children}</div>
  </section>
);

const List = ({ items }) => (
  <ul className="list-disc pl-5 space-y-1.5 marker:text-brass">
    {items.map((it) => <li key={it}>{it}</li>)}
  </ul>
);

export default function PrivacyPolicy() {
  useSeo({
    title: "Privacy Notice | Emerald Crown Property Investments Ltd",
    description: "How Emerald Crown Property Investments Ltd collects, uses, stores and shares personal information, including enquiries received through Meta, Facebook and Instagram Lead Ads, under UK data-protection law.",
    path: "/privacy-policy",
  });
  useEffect(() => { track("page_view", { page: "privacy-policy" }); }, []);

  return (
    <div data-testid="privacy-policy-page">
      {/* Dark emerald brand header */}
      <header className="bg-emerald-950 text-white pt-32 pb-14">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="font-mono text-xs uppercase tracking-[0.25em] text-brass">Legal</span>
          <h1 className="mt-4 text-4xl sm:text-5xl font-serif font-semibold" data-testid="privacy-policy-title">Privacy Notice</h1>
          <p className="mt-4 text-emerald-100/70 text-sm">Last updated: 30 September 2026</p>
        </div>
      </header>

      {/* Readable content body */}
      <div className="bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-20 space-y-10">

          <Section id="introduction" title="Introduction">
            <p>Emerald Crown Property Investments Ltd respects the privacy of everyone who contacts or interacts with our business. This notice explains, in clear and plain UK English, how we collect, use, store and share personal information, and the rights you have in relation to that information.</p>
            <p>We have tried to avoid unnecessarily complicated legal language. If anything is unclear, or you would like more detail about how we handle your information, please contact us at <a href="mailto:hello@emeraldcrown.co.uk" className="text-emerald-800 underline">hello@emeraldcrown.co.uk</a>.</p>
          </Section>

          <Section id="who-we-are" title="Who we are">
            <p>Emerald Crown Property Investments Ltd is responsible for determining how the personal information covered by this notice is handled where it acts as the "data controller".</p>
            <ul className="space-y-1.5">
              <li><strong className="text-slate-800">Company:</strong> Emerald Crown Property Investments Ltd</li>
              <li><strong className="text-slate-800">Company number:</strong> 16399664</li>
              <li><strong className="text-slate-800">Registered office:</strong> 124-128 City Road, London, England, EC1V 2NX</li>
              <li><strong className="text-slate-800">Email:</strong> <a href="mailto:hello@emeraldcrown.co.uk" className="text-emerald-800 underline">hello@emeraldcrown.co.uk</a></li>
              <li><strong className="text-slate-800">Telephone:</strong> <a href="tel:+447897674550" className="text-emerald-800 underline">07897 674550</a></li>
              <li><strong className="text-slate-800">ICO registration number:</strong> ZC242913</li>
            </ul>
          </Section>

          <Section id="what-we-collect" title="What information we may collect">
            <p>Depending on your relationship and interaction with Emerald Crown, the personal information we hold may include:</p>
            <List items={[
              "Name",
              "Telephone number",
              "Email address",
              "Correspondence and enquiry information",
              "Property address and postcode where supplied",
              "Property details",
              "Information about a proposed property sale",
              "Current marketing / estate-agent status of a property",
              "Desired sale timeframe",
              "Information you voluntarily provide about the reason or circumstances surrounding a proposed sale",
              "Investor and company details",
              "Investor buying criteria",
              "Investment strategy",
              "Budget and funding information",
              "AML / KYC and identity-verification information where required",
              "Source-of-funds and source-of-wealth information where required",
              "Records of communications and interactions with Emerald Crown",
              "Technical information generated through use of our website, where applicable",
              "Marketing preferences, where applicable",
            ]} />
            <p>We do not set out to collect information that our website and business do not actually use.</p>
          </Section>

          <Section id="how-collected" title="How information is collected">
            <p>We may collect personal information through:</p>
            <List items={[
              "Emerald Crown website forms",
              "Direct telephone calls",
              "Email",
              "Social media enquiries",
              "Facebook and Instagram",
              "Meta Lead Ads / Instant Forms",
              "Facebook Marketplace enquiries",
              "Referrals and introducers",
              "Property enquiries",
              "Investor registration and onboarding",
              "Meetings and appointments",
              "AML / KYC processes",
              "Third-party service providers, where lawful and appropriate",
            ]} />
          </Section>

          <Section id="meta-lead-ads" title="Meta / Facebook / Instagram Lead Ads">
            <p>Emerald Crown may advertise through Meta platforms, including Facebook and Instagram.</p>
            <p>When you submit a Meta Lead Ad or Instant Form, Meta may provide us with the information you chose to submit, which may include your:</p>
            <List items={[
              "Name",
              "Telephone number",
              "Email address",
              "Answers to our lead-form questions",
            ]} />
            <p>We will use this information primarily to:</p>
            <List items={[
              "Review your enquiry",
              "Contact you about your property enquiry",
              "Understand what you are looking to achieve",
              "Discuss potentially suitable property-sale routes or Emerald Crown services",
              "Administer and follow up your enquiry",
            ]} />
            <p>Your use of Meta's platforms is also subject to Meta's own privacy arrangements. Emerald Crown does not control Meta's independent processing of personal information, and Meta does not endorse, and is not affiliated with, Emerald Crown.</p>
          </Section>

          <Section id="how-we-use" title="How we use personal information">
            <p>We use personal information for appropriate purposes, which may include:</p>
            <List items={[
              "Responding to enquiries",
              "Discussing potential property transactions",
              "Assessing whether Emerald Crown may be able to assist a property owner",
              "Arranging calls, appointments and follow-ups",
              "Introducing suitable properties to relevant services or partners where appropriate and lawful",
              "Managing investor relationships",
              "Matching approved investors with opportunities",
              "Performing AML / KYC and compliance obligations",
              "Managing contracts and business relationships",
              "Maintaining our CRM records",
              "Handling complaints",
              "Preventing fraud and protecting the business",
              "Complying with legal and regulatory obligations",
              "Improving our services",
              "Sending marketing communications where permitted by law",
            ]} />
          </Section>

          <Section id="lawful-bases" title="Lawful bases">
            <p>The lawful basis we rely on will depend on the circumstances and may include:</p>
            <List items={[
              "Taking steps at your request before entering into a contract",
              "Performance of a contract",
              "Compliance with legal obligations",
              "Our legitimate interests, where those interests are not overridden by your rights and freedoms",
              "Consent, where consent is legally required",
            ]} />
            <p>Consent is not the basis for all of our processing; in many cases we rely on one of the other lawful bases above.</p>
          </Section>

          <Section id="vendor-enquiries" title="Property vendor enquiries">
            <p>When you submit a property enquiry, we may use your information to contact you, understand your circumstances and assess potential routes for the property.</p>
            <p>Depending on suitability, potential routes may include:</p>
            <List items={[
              "Emerald Crown considering a direct purchase",
              "Property auction",
              "Another appropriate property-sale or introduction route",
            ]} />
            <p>We do not promise that Emerald Crown will purchase, sell or successfully dispose of any property. Any suitable route is discussed with you as part of the enquiry process.</p>
          </Section>

          <Section id="investor-aml" title="Investor data and AML / KYC">
            <p>Where you interact with us as an investor, we may process your information for investor registration and qualification, AML / KYC checks, sanctions and politically-exposed-person (PEP) screening, source-of-funds checks where required, administration of the investor relationship, and matching approved investors with opportunities.</p>
            <p>Some of this information may need to be retained in order to comply with our legal and regulatory requirements.</p>
          </Section>

          <Section id="sharing" title="Sharing personal information">
            <p>We may share personal information where there is an appropriate purpose and a lawful basis to do so. Depending on the circumstances, relevant categories of recipient may include:</p>
            <List items={[
              "IT, website, CRM, hosting and communications providers",
              "AML / KYC providers",
              "Professional advisers",
              "Solicitors",
              "Accountants",
              "Insurers",
              "Property professionals",
              "Estate agents",
              "Auction houses",
              "Brokers or other service providers where relevant",
              "Regulators, law-enforcement bodies or public authorities where legally required",
            ]} />
            <p>We do not automatically share every enquiry with third parties. In particular, your property or vendor details will not automatically be passed to an auction house simply because you complete a Meta form. Any relevant introduction is discussed with you as part of the enquiry process, where appropriate.</p>
          </Section>

          <Section id="retention" title="Data retention">
            <p>We retain personal information only for as long as is reasonably necessary for the purpose for which it was collected, and to meet applicable legal, regulatory, contractual, accounting, insurance or dispute-resolution requirements.</p>
            <p>Because different types of information are subject to different requirements, retention periods will vary rather than following a single universal period.</p>
          </Section>

          <Section id="security" title="Data security">
            <p>We use reasonable organisational and technical measures designed to protect personal information against unauthorised access, alteration, disclosure or loss.</p>
          </Section>

          <Section id="international" title="International processing">
            <p>Some of our technology and service providers may process data outside the United Kingdom. Where this happens and it is required, we will rely on appropriate safeguards recognised under UK data-protection law.</p>
          </Section>

          <Section id="your-rights" title="Your rights">
            <p>Subject to legal conditions and exemptions, you may have the following rights under UK GDPR and related data-protection law:</p>
            <List items={[
              "The right to be informed",
              "The right of access",
              "The right to rectification",
              "The right to erasure",
              "The right to restrict processing",
              "The right to object",
              "The right to data portability",
              "Rights relating to certain automated decision-making",
            ]} />
            <p>These rights do not apply in all circumstances and may be subject to conditions and exemptions.</p>
          </Section>

          <Section id="privacy-requests" title="Privacy requests">
            <p>If you wish to make a privacy or data-protection request, or have any questions about this notice, please contact us at <a href="mailto:hello@emeraldcrown.co.uk" className="text-emerald-800 underline">hello@emeraldcrown.co.uk</a>.</p>
          </Section>

          <Section id="ico" title="Complaints to the ICO">
            <p>If you are unhappy with how we have handled your personal information, you have the right to complain to the Information Commissioner's Office (ICO).</p>
            <p>Our ICO registration number is <strong className="text-slate-800">ZC242913</strong>. Registration with the ICO does not constitute approval or endorsement of Emerald Crown by the ICO.</p>
          </Section>

          <Section id="marketing" title="Direct marketing">
            <p>We may send you relevant marketing communications where we are legally permitted to do so. You can opt out of electronic marketing communications at any time.</p>
            <p>Opting out of marketing does not necessarily prevent us from sending you necessary service or transaction-related communications.</p>
          </Section>

          <Section id="cookies" title="Cookies">
            <p>Our website uses essential cookies that are necessary for it to function and, where you agree, optional analytics cookies that help us understand how the site is used. You can manage your choice through our cookie banner by selecting "Accept all" or "Essential only", and you can also control cookies through your browser settings.</p>
            <p>We do not currently operate third-party advertising or tracking pixels (such as the Meta Pixel) on this website. If this changes, we will update this notice and our cookie banner accordingly and obtain any consent required by law. This notice is intended to work consistently with our cookie consent mechanism.</p>
          </Section>

          <Section id="changes" title="Changes to this Privacy Notice">
            <p>We may update this notice from time to time. The current version, together with its "last updated" date, will always appear on this page.</p>
          </Section>

          <p className="text-sm text-slate-400 border-t border-slate-200 pt-6">Emerald Crown Property Investments Ltd · Company number 16399664 · Registered office 124-128 City Road, London, England, EC1V 2NX · ICO registration ZC242913.</p>
        </div>
      </div>
    </div>
  );
}
